// React
import React, { FC, useState, useEffect } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Classes
import { Accordion } from 'Components/Accordion';
import { areEqual } from 'Utils/areEqual';
import withErrorBoundary from 'HOC/ErrorBoundary';
import {
  VisitorTypeReducerState,
  ProspectTree,
} from './VisitorType/classes/visitorType.classes';
import { EthnicityReducerState } from './Ethnicity/classes/ethnicity.classes';
import { GenderReducerState } from './Gender/classes/gender.classes';

// Actions
import { initializeVisitorTypeState } from './VisitorType/actions/visitorType.actions';
import { initializeGenderState } from './Gender/actions/gender.actions';
import { initializeEthnicityState } from './Ethnicity/actions/ethnicity.actions';

// Components
import { VisitorType } from './VisitorType';
import { Geography } from './Geography';
import { Age } from './Age';
import { Gender } from './Gender';
import { Ethnicity } from './Ethnicity';

interface Props {}
const genderOverrideStylesChildContainer = {
  marginLeft: '18px',
};
const ethnicityOverrideStylesChildContainer = {
  marginLeft: '18px',
};
const ageOverrideStylesChildContainer = {
  marginLeft: '17px',
};

const Demographics: FC<Props> = () => {
  const { demographics } = useSelector(
    (selectors: any) => selectors.filter,
    areEqual
  );
  const {
    prospectiveStudentsQuery,
    parentsOfProspectsQuery,
    otherQuery,
    usaQuery,
    internationalQuery,
    ageQuery,
    genderQuery,
    ethnicityQuery,
  } = useSelector((selectors: any) => selectors.filter.query, areEqual);
  const dispatch = useDispatch();

  const [visitorTypeAccordion, setVisitorTypeAccordion] = useState(
    prospectiveStudentsQuery !== '' ||
      parentsOfProspectsQuery !== '' ||
      otherQuery !== ''
  );
  const [geographyAccordion, setGeographyAccordion] = useState(
    usaQuery !== '' || internationalQuery !== ''
  );
  const [ageAccordion, setAgeAccordion] = useState(ageQuery !== '');
  const [genderAccordion, setGenderAccordion] = useState(genderQuery !== '');
  const [ethnicityAccordion, setEthnicityAccordion] = useState(
    ethnicityQuery !== ''
  );
  const [selectAllEthnicity, setSelectAllEthnicity] = useState(false);
  const [selectAllGender, setSelectAllGender] = useState(false);
  useEffect(() => {
    if (
      prospectiveStudentsQuery !== '' ||
      parentsOfProspectsQuery !== '' ||
      otherQuery !== ''
    )
      setVisitorTypeAccordion(
        prospectiveStudentsQuery !== '' ||
          parentsOfProspectsQuery !== '' ||
          otherQuery !== ''
      );
  }, [prospectiveStudentsQuery, parentsOfProspectsQuery, otherQuery]);

  useEffect(() => {
    if (usaQuery !== '' || internationalQuery !== '')
      setGeographyAccordion(usaQuery !== '' || internationalQuery !== '');
  }, [usaQuery, internationalQuery]);

  useEffect(() => {
    if (ageQuery !== '') setAgeAccordion(ageQuery !== '');
    if (ethnicityQuery !== '') setEthnicityAccordion(ethnicityQuery !== '');
  }, [ageQuery]);

  useEffect(() => {
    if (genderQuery !== '') setGenderAccordion(genderQuery !== '');
  }, [genderQuery]);

  useEffect(() => {
    if (ethnicityQuery !== '') setEthnicityAccordion(ethnicityQuery !== '');
  }, [ethnicityQuery]);

  // Setting function expressions for methods used in the Accordions
  const visitorTypeAccordionCallBack = () => {
    setVisitorTypeAccordion(!visitorTypeAccordion);
  };
  const visitorTypeAccordionIsActive =
    prospectiveStudentsQuery !== '' ||
    parentsOfProspectsQuery !== '' ||
    otherQuery !== '';
  const visitorTypeAccordionClear = () => {
    const allFalseProspectTree = new ProspectTree();
    const newVisitorTypeState = new VisitorTypeReducerState(
      allFalseProspectTree,
      allFalseProspectTree
    );
    initializeVisitorTypeState(dispatch, newVisitorTypeState);
  };
  const geographyAccordionCallBack = () => {
    setGeographyAccordion(!geographyAccordion);
  };
  const geographyIsActive = usaQuery !== '' || internationalQuery !== '';
  const ageAccordionCallBack = () => {
    setAgeAccordion(!ageAccordion);
  };
  const ageIsActive = ageQuery !== '';
  const genderAccordionCallBack = () => {
    setGenderAccordion(!genderAccordion);
  };
  const genderIsActive = genderQuery !== '';
  const genderUseClearButton = genderQuery !== '';
  const genderUserSelectAllButton =
    !demographics.gender.male ||
    !demographics.gender.female ||
    !demographics.gender.otherThirdGender;
  const genderSelectAll = () => {
    const newGenderState = new GenderReducerState(true, true, true);
    initializeGenderState(dispatch, newGenderState);
    setSelectAllGender(true);
  };
  const genderClear = () => {
    const newGenderState = new GenderReducerState(false, false, false);
    initializeGenderState(dispatch, newGenderState);
    setSelectAllGender(false);
  };
  const ethnicityAccordionCallback = () => {
    setEthnicityAccordion(!ethnicityAccordion);
  };
  const ethnicityIsActive = ethnicityQuery !== '';
  const ethnicityUseClearButton = ethnicityQuery !== '';
  const ethnicityUseSelectButton =
    !demographics.ethnicity.americanIndianOrAlaskaNative ||
    !demographics.ethnicity.asian ||
    !demographics.ethnicity.blackOrAfricanAmerican ||
    !demographics.ethnicity.hispanicOrLatin ||
    !demographics.ethnicity.nativeHawaiianOrOtherPacificIslander ||
    !demographics.ethnicity.white ||
    !demographics.ethnicity.mixedRace ||
    !demographics.ethnicity.other;
  const ethnicitySelectAll = () => {
    const newEthnicityState = new EthnicityReducerState(
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      true
    );
    initializeEthnicityState(dispatch, newEthnicityState);
    setSelectAllEthnicity(true);
  };
  const ethnicityClear = () => {
    const newEthnicityState = new EthnicityReducerState(
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false
    );
    initializeEthnicityState(dispatch, newEthnicityState);
    setSelectAllEthnicity(false);
  };

  return (
    <ul>
      <li>
        <Accordion
          title="Visitor Type"
          id="visitorType"
          isOpen={visitorTypeAccordion}
          accordionCallback={visitorTypeAccordionCallBack}
          useTopDivider={false}
          useBottomDivider={false}
          accordionType="Section"
          isActive={visitorTypeAccordionIsActive}
          clear={visitorTypeAccordionClear}
        >
          <VisitorType />
        </Accordion>
      </li>
      <li>
        <Accordion
          title="Geography"
          id="geography"
          isOpen={geographyAccordion}
          accordionCallback={geographyAccordionCallBack}
          useTopDivider={false}
          useBottomDivider={false}
          accordionType="Section"
          isActive={geographyIsActive}
        >
          <Geography />
        </Accordion>
      </li>
      <li>
        <Accordion
          title="Age"
          id="age"
          isOpen={ageAccordion}
          accordionCallback={ageAccordionCallBack}
          useTopDivider={false}
          useBottomDivider={false}
          accordionType="Section"
          isActive={ageIsActive}
          overrideStylesChildContainer={ageOverrideStylesChildContainer}
        >
          <Age />
        </Accordion>
      </li>
      <li>
        <Accordion
          title="Gender"
          id="gender"
          isOpen={genderAccordion}
          accordionCallback={genderAccordionCallBack}
          useTopDivider={false}
          useBottomDivider={false}
          accordionType="Section"
          isActive={genderIsActive}
          useClearButton={genderUseClearButton}
          useSelectAllButton={genderUserSelectAllButton}
          selectAll={genderSelectAll}
          clear={genderClear}
          overrideStylesChildContainer={genderOverrideStylesChildContainer}
        >
          <Gender selectAllGender={selectAllGender} />
        </Accordion>
      </li>
      <li>
        <Accordion
          title="Ethnicity"
          id="ethnicity"
          isOpen={ethnicityAccordion}
          accordionCallback={ethnicityAccordionCallback}
          useTopDivider={false}
          useBottomDivider={false}
          accordionType="Section"
          isActive={ethnicityIsActive}
          useClearButton={ethnicityUseClearButton}
          useSelectAllButton={ethnicityUseSelectButton}
          selectAll={ethnicitySelectAll}
          clear={ethnicityClear}
          overrideStylesChildContainer={ethnicityOverrideStylesChildContainer}
        >
          <Ethnicity selectAllEthnicity={selectAllEthnicity} />
        </Accordion>
      </li>
    </ul>
  );
};

export default withErrorBoundary(Demographics);
